import { Injectable, computed, inject } from '@angular/core';

import { AuthStore } from '../auth/auth.store';
import type { AccessRequirement, AppModule, FeatureFlag, Permission, Role } from './access.models';

@Injectable({ providedIn: 'root' })
export class AccessService {
  private readonly auth = inject(AuthStore);

  private readonly roles = computed(() => new Set<Role>(this.auth.user()?.roles ?? []));
  private readonly permissions = computed(
    () => new Set<Permission>(this.auth.user()?.permissions ?? []),
  );
  private readonly flags = computed(
    () => new Set<FeatureFlag>(this.auth.user()?.featureFlags ?? []),
  );

  /** Null while the backend has not reported apps: see `AuthUser.apps`. */
  private readonly apps = computed(() => {
    const apps = this.auth.user()?.apps;

    return apps ? new Set<AppModule>(apps) : null;
  });

  hasRole(role: Role): boolean {
    return this.roles().has(role);
  }

  can(permission: Permission): boolean {
    return this.permissions().has(permission);
  }

  /** Whether the selected workspace has the application rented and on. */
  hasApp(app: AppModule): boolean {
    const apps = this.apps();

    return apps === null || apps.has(app);
  }

  isEnabled(flag: FeatureFlag): boolean {
    return this.flags().has(flag);
  }

  satisfies(requirement: AccessRequirement | undefined): boolean {
    if (!requirement) {
      return true;
    }

    const {
      roles = [],
      permissions = [],
      withoutPermissions = [],
      featureFlags = [],
      apps = [],
      anyApps = [],
    } = requirement;

    return (
      roles.every((role) => this.hasRole(role)) &&
      permissions.every((permission) => this.can(permission)) &&
      !withoutPermissions.some((permission) => this.can(permission)) &&
      featureFlags.every((flag) => this.isEnabled(flag)) &&
      apps.every((app) => this.hasApp(app)) &&
      (anyApps.length === 0 || anyApps.some((app) => this.hasApp(app)))
    );
  }
}
